import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  FieldValuePageSchema,
  type FieldValueOption,
} from "@forgepoint/shared";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { EntitySelector } from "@/components/EntitySelector";
import { fetchApi } from "@/lib/api";
import { JSONValue } from "@/components/jsonSchemaForm/types";
import { ActionConfiguration } from "@/store/agentsStore";

/** A loaded option, addressed by its stringified value inside the selector. */
interface DynamicFieldEntity {
  id: string;
  label: string;
  value: FieldValueOption["value"];
}

/** The query a loaded page answered, and the cursor that continues it. */
interface LoadedPage {
  query?: string;
  cursor?: string;
}

interface DynamicFieldProps {
  actionId?: string;
  triggerId?: string;
  fieldName: string;
  placeholder?: string;
  value?: JSONValue;
  existingFieldConfiguration?: ActionConfiguration[];
  connectionId?: string;
  shouldRefresh?: boolean;
  onChange: (option: { input: JSONValue; label?: string }) => void;
  onRefresh?: () => void;
}

const EMPTY_FIELD_CONFIGURATION: ActionConfiguration[] = [];

const numberPattern = /^-?\d+(\.\d+)?$/;

const FETCH_ERROR = "Failed to fetch field options";

function formatValueForCustomInput(inputValue: JSONValue): string {
  if (inputValue === undefined || inputValue === null) {
    return "";
  }

  if (typeof inputValue === "string") {
    return inputValue;
  }

  if (typeof inputValue === "number" || typeof inputValue === "boolean") {
    return String(inputValue);
  }

  if (Array.isArray(inputValue) || typeof inputValue === "object") {
    try {
      return JSON.stringify(inputValue, null, 2);
    } catch {
      return "";
    }
  }

  return "";
}

function parseCustomInput(rawValue: string): JSONValue {
  const trimmed = rawValue.trim();

  if (trimmed.length === 0) {
    return undefined;
  }

  if (trimmed === "true") {
    return true;
  }

  if (trimmed === "false") {
    return false;
  }

  if (trimmed === "null") {
    return null;
  }

  if (numberPattern.test(trimmed)) {
    return Number(trimmed);
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    return rawValue;
  }
}

/** Only a primitive value can name an option; anything else is a custom value. */
function toOptionId(value: JSONValue): string | null {
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }
  return null;
}

export function DynamicField({
  actionId,
  triggerId,
  fieldName,
  placeholder = "Select an option",
  value,
  existingFieldConfiguration = EMPTY_FIELD_CONFIGURATION,
  connectionId,
  shouldRefresh = false,
  onChange,
  onRefresh,
}: DynamicFieldProps) {
  const [loadedOptions, setLoadedOptions] = useState<
    Map<string, DynamicFieldEntity>
  >(new Map());
  const [loadedPage, setLoadedPage] = useState<LoadedPage | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<"custom" | "options">("options");
  const [customValue, setCustomValue] = useState("");
  const customChangeTimeout = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const loadPage = useCallback(
    async (query?: string, cursor?: string): Promise<string[]> => {
      if (!actionId && !triggerId) {
        setError("Either actionId or triggerId is required");
        return [];
      }

      try {
        const response = await fetchApi("apps/fields/options", "POST", {
          body: {
            actionId,
            triggerId,
            fieldName,
            existingFieldConfiguration,
            connectionId,
            query,
            cursor,
          },
        });

        const parsed = FieldValuePageSchema.safeParse(response.data);
        if (!response.ok || !parsed.success) {
          setError(response.error ?? FETCH_ERROR);
          return [];
        }

        setError(null);
        setLoadedPage({ query, cursor: parsed.data.nextCursor });
        setLoadedOptions((current) =>
          mergeOptions(current, parsed.data.options),
        );
        return parsed.data.options.map((option) => String(option.value));
      } catch (fetchError) {
        setError(
          fetchError instanceof Error ? fetchError.message : FETCH_ERROR,
        );
        return [];
      }
    },
    [actionId, triggerId, fieldName, existingFieldConfiguration, connectionId],
  );

  const loadFirstPage = useCallback(
    async (query?: string): Promise<string[]> => {
      setIsLoading(true);
      try {
        return await loadPage(query, undefined);
      } finally {
        setIsLoading(false);
      }
    },
    [loadPage],
  );

  const loadNextPage = useCallback(
    async (query?: string): Promise<string[]> => {
      if (!loadedPage?.cursor || loadedPage.query !== query) {
        return [];
      }
      setIsLoadingMore(true);
      try {
        return await loadPage(query, loadedPage.cursor);
      } finally {
        setIsLoadingMore(false);
      }
    },
    [loadPage, loadedPage],
  );

  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage]);

  const selectedId = toOptionId(value);

  const entities = useMemo(() => {
    if (selectedId === null || loadedOptions.has(selectedId)) {
      return loadedOptions;
    }
    return new Map(loadedOptions).set(selectedId, {
      id: selectedId,
      label: selectedId,
      value: selectedId,
    });
  }, [loadedOptions, selectedId]);

  // Only an unsearched, single-page list proves a value is not among the options.
  const isCompleteList =
    loadedPage !== null &&
    loadedPage.query === undefined &&
    loadedPage.cursor === undefined;
  const hasMatchingOption =
    selectedId !== null && loadedOptions.has(selectedId);

  useEffect(() => {
    if (mode !== "options" || value === undefined || value === null) {
      return;
    }
    if (!isCompleteList || hasMatchingOption) {
      return;
    }
    setMode("custom");
    setCustomValue(formatValueForCustomInput(value));
  }, [mode, isCompleteList, hasMatchingOption, value]);

  useEffect(() => {
    if (mode === "custom") {
      setCustomValue(formatValueForCustomInput(value));
    }
  }, [mode, value]);

  const handleSelectId = useCallback(
    (id: string | null) => {
      if (id === null) {
        onChange({ input: undefined });
        return;
      }

      const entity = entities.get(id);
      if (!entity) {
        return;
      }

      onChange({ input: entity.value, label: entity.label });

      if (shouldRefresh && onRefresh) {
        onRefresh();
      }
    },
    [entities, onChange, shouldRefresh, onRefresh],
  );

  const emitCustomChange = useCallback(
    (nextValue: string) => {
      if (customChangeTimeout.current) {
        clearTimeout(customChangeTimeout.current);
      }
      customChangeTimeout.current = setTimeout(() => {
        onChange({ input: parseCustomInput(nextValue) });
      }, 300);
    },
    [onChange],
  );

  const handleCustomChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const nextValue = event.target.value;
      setCustomValue(nextValue);
      emitCustomChange(nextValue);
    },
    [emitCustomChange],
  );

  const toggleMode = useCallback(() => {
    if (mode === "custom") {
      setMode("options");
      if (!hasMatchingOption) {
        onChange({ input: undefined });
      }
      return;
    }

    setMode("custom");
    setCustomValue(formatValueForCustomInput(value));
  }, [hasMatchingOption, mode, onChange, value]);

  useEffect(() => {
    return () => {
      if (customChangeTimeout.current) {
        clearTimeout(customChangeTimeout.current);
      }
    };
  }, []);

  const handleRefresh = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      loadFirstPage();
    },
    [loadFirstPage],
  );

  return (
    <div className="w-full space-y-2">
      <div className="flex items-start gap-2">
        <div className="flex-1">
          {mode === "custom" ? (
            <Input
              value={customValue}
              placeholder={placeholder}
              onChange={handleCustomChange}
            />
          ) : (
            <EntitySelector<DynamicFieldEntity>
              entities={entities}
              listLoading={isLoading}
              fetchEntities={loadFirstPage}
              getDisplayName={(entity) => entity.label}
              currentlySelectedId={selectedId}
              onSelectId={handleSelectId}
              placeholder={placeholder}
              searchPlaceholder="Search options..."
              entityLabel="options"
              onLoadMore={loadNextPage}
              hasMore={loadedPage?.cursor !== undefined}
              isLoadingMore={isLoadingMore}
            />
          )}
        </div>

        {mode === "options" && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
          </Button>
        )}
      </div>

      <div className="flex items-center justify-end text-xs text-muted-foreground">
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0 text-xs text-muted-foreground"
          onClick={toggleMode}
        >
          {mode === "custom" ? "Use dynamic value" : "Use custom value"}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}

/** Keeps every option seen so far, so a selected label survives a new search. */
function mergeOptions(
  current: Map<string, DynamicFieldEntity>,
  options: FieldValueOption[],
): Map<string, DynamicFieldEntity> {
  const merged = new Map(current);
  for (const option of options) {
    const id = String(option.value);
    merged.set(id, { id, label: option.label, value: option.value });
  }
  return merged;
}
